import { useState, useEffect, useCallback } from "react";
import { api } from "../App";
import { getApiPrefix } from '../utils/apiHelper';
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Shield, Search, Filter, ChevronLeft, ChevronRight } from "lucide-react";
import { PageLoader } from "../components/ui/page-loader";
import { useTranslation } from "../contexts/I18nContext";

export default function AuditTrailPage() {
  const { t } = useTranslation();
  const [data, setData] = useState({ items: [], total: 0, page: 1, total_pages: 0 });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [tipo, setTipo] = useState("all");

  const load = useCallback(async (p = page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, page_size: 30 });
      if (search) params.set("search", search);
      if (tipo !== "all") params.set("tipo", tipo);
      const [res, st] = await Promise.all([
        api.get(`${getApiPrefix()}/audit-trail?${params}`),
        api.get(`${getApiPrefix()}/audit-trail/stats`),
      ]);
      setData(res.data);
      setStats(st.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [page, search, tipo]);

  useEffect(() => { load(1); }, [tipo, load]);
  useEffect(() => { load(page); }, [page, load]);

  const handleSearch = () => { setPage(1); load(1); };

  const actionColors = {
    creazione: "bg-emerald-100 text-emerald-800",
    modifica: "bg-blue-100 text-blue-800",
    eliminazione: "bg-red-100 text-red-800",
    login: "bg-violet-100 text-violet-800",
    approvazione: "bg-amber-100 text-amber-800",
  };

  return (
    <div className="space-y-5" data-testid="audit-trail-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Shield className="h-6 w-6" />{t('common.auditTrail')}</h1>
          <p className="text-muted-foreground text-sm">Registro completo di tutte le operazioni</p>
        </div>
        {stats && (
          <div className="flex items-center gap-3">
            <Badge variant="outline">{stats.total} totali</Badge>
            <Badge variant="outline" className="bg-blue-50">{stats.oggi} oggi</Badge>
          </div>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Cerca per descrizione, entita, utente..." value={search}
                onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearch()}
                className="pl-10" data-testid="audit-search" />
            </div>
            <Select value={tipo} onValueChange={setTipo}>
              <SelectTrigger className="w-40" data-testid="audit-filter-tipo"><SelectValue placeholder={t("common.type")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common.allTypes')}</SelectItem>
                <SelectItem value="fattura">{t('invoices.title')}</SelectItem>
                <SelectItem value="movimento">Movimento</SelectItem>
                <SelectItem value="scadenza">{t('invoices.dueDate')}</SelectItem>
                <SelectItem value="approvazione">Approvazione</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleSearch} variant="outline"><Filter className="h-4 w-4 mr-1" />{t('common.filter')}</Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="pt-4">
          {loading ? <PageLoader variant="section" /> : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b bg-muted/30">
                    <th className="text-left py-2.5 px-3">Data/Ora</th>
                    <th className="text-left py-2.5 px-3">{t('common.user')}</th>
                    <th className="text-left py-2.5 px-3">{t('common.type')}</th>
                    <th className="text-left py-2.5 px-3">Azione</th>
                    <th className="text-left py-2.5 px-3">Entita</th>
                    <th className="text-left py-2.5 px-3">{t('common.description')}</th>
                  </tr></thead>
                  <tbody>
                    {data.items.length > 0 ? data.items.map((item, i) => (
                      <tr key={item.id || `audit-${i}`} className="border-b hover:bg-muted/20">
                        <td className="py-2 px-3 text-xs text-muted-foreground whitespace-nowrap">
                          {item.created_at ? new Date(item.created_at).toLocaleString('it-IT') : '-'}
                        </td>
                        <td className="py-2 px-3 font-medium">{item.utente_nome || '-'}</td>
                        <td className="py-2 px-3"><Badge variant="outline" className="text-xs">{item.tipo || '-'}</Badge></td>
                        <td className="py-2 px-3">
                          <Badge className={`text-xs ${actionColors[item.azione] || 'bg-slate-100 text-slate-800'}`}>{item.azione || '-'}</Badge>
                        </td>
                        <td className="py-2 px-3">{item.entita || '-'}</td>
                        <td className="py-2 px-3 max-w-[300px] truncate">{item.descrizione || '-'}</td>
                      </tr>
                    )) : (
                      <tr><td colSpan={6} className="py-12 text-center text-muted-foreground">
                        Nessuna attivita registrata. Le operazioni future verranno tracciate qui.
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {data.total_pages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-xs text-muted-foreground">Pagina {data.page} di {data.total_pages} ({data.total} risultati)</p>
                  <div className="flex gap-1">
                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="h-4 w-4" /></Button>
                    <Button variant="outline" size="sm" disabled={page >= data.total_pages} onClick={() => setPage(p => p + 1)}><ChevronRight className="h-4 w-4" /></Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
